import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";
import { PREMIUM } from "@/constants/testIds/premium";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let active = true;

    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("success");
          return;
        }
      } catch (error) {
        console.error("payment status poll failed, will retry:", error);
      }
      if (attempts.current >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <div data-testid={PREMIUM.successStatus} className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center vc-fade-up">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-emerald-400" />
            <h1 className="mt-4 text-xl font-extrabold text-white">Confirmando seu pagamento…</h1>
            <p className="mt-1 text-sm text-slate-400">Estamos validando a assinatura com o Stripe. Um instante.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-amber-400/15 text-amber-300">
              <Crown className="h-9 w-9" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-white">Premium ativado! 🎉</h1>
            <p className="mt-1 text-sm text-slate-400">Agora você tem questões e simulados ilimitados. Seus 7 dias grátis começaram.</p>
            <button data-testid="payment-success-continue" onClick={() => navigate("/inicio")}
              className="mt-6 w-full rounded-xl bg-emerald-500 py-3 text-sm font-bold text-white hover:bg-emerald-600 active:scale-95">
              Começar a estudar
            </button>
          </>
        )}
        {state === "timeout" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="mt-4 text-xl font-extrabold text-white">Pagamento recebido</h1>
            <p className="mt-1 text-sm text-slate-400">A confirmação pode levar alguns instantes. Atualize a página inicial em breve.</p>
            <button onClick={() => navigate("/inicio")} className="mt-6 w-full rounded-xl bg-slate-700 py-3 text-sm font-bold text-white hover:bg-slate-600">
              Ir para o início
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <h1 className="mt-4 text-xl font-extrabold text-white">Sessão inválida</h1>
            <button onClick={() => navigate("/premium")} className="mt-6 w-full rounded-xl bg-slate-700 py-3 text-sm font-bold text-white hover:bg-slate-600">
              Voltar ao Premium
            </button>
          </>
        )}
      </div>
    </div>
  );
}
